import * as React from "react";

import type { FilterDefinition } from "@/shared/components/ResourceView";
import {
  ChartDisplayType,
  NUMERIC_AGGREGATIONS,
  NumericAggregation,
} from "@/shared/domain/metrics";
import {
  Combobox,
  TextInput,
  Toggle,
  ToggleGroup,
} from "@/shared/foundation/primitives";

import { getCardTitle } from "../cardTitle";
import {
  CardConfig,
  ChartCardConfig,
} from "../DashboardHeader/DashboardParams";

import { SignalColorDot } from "./SignalColorDot";
import {
  DEFAULT_AGGREGATION,
  DEFAULT_DISPLAY,
  displayOptionsFor,
  rawDisplay,
} from "./widgetEditorMode";
import styles from "./WidgetFields.module.css";

/** The option that clears a card's grouping.
 *
 *  Safe as a sentinel because every groupable value is an entity-prefixed wire
 *  path (`device.device_id`, `session.custom.<name>`) and so always contains a
 *  dot — a bare phrase cannot collide with one. Never sent anywhere. */
const NO_GROUPING = "No grouping";

/** The two things a chart card can plot. Not a persisted enum: the card
 *  itself says which it is by carrying an aggregation or not — this is only
 *  the picker's own vocabulary.
 *
 *  The labels name what happens to the SESSIONS in a period, which is the
 *  choice being made: collapse them into one value, or plot every one. */
const SERIES_OPTIONS = [
  { value: "aggregated", label: "Bucket" },
  { value: "raw", label: "Record" },
] as const;

type SeriesKind = (typeof SERIES_OPTIONS)[number]["value"];

/** What each series actually does to the data, and what it is good for.
 *  Keyed by `SeriesKind` so a new option cannot ship without its own copy. */
const SERIES_HINTS: Record<SeriesKind, string> = {
  aggregated:
    "Records are grouped by the dashboard period and summarized. Best for trends.",
  raw: "Raw values, nothing summarized. Best for spotting outliers and individual records.",
};

export interface WidgetFieldsProps {
  /** The draft being configured (controlled by the parent). */
  card: CardConfig;
  /** Every selectable metric name. */
  allNames: string[];
  /** The metric to show selected — the card's own, or its resolved fallback
   *  when unset or stale. */
  effectiveMetric: string;
  /**
   * The fields a chart may be split by, derived from the dashboard's own
   * filter menu (see `groupableFieldDefinitions`). Empty or omitted — an org
   * with no device rows and no discrete custom fields — hides the control
   * entirely rather than offering an empty picker.
   */
  groupableFields?: FilterDefinition[];
  /** Fired with the updated draft whenever the user changes a control. */
  onChange: (next: CardConfig) => void;
}

/**
 * A widget's title, then its data pickers — metric, and then whatever else
 * its kind and series have to say for themselves.
 *
 * A chart additionally chooses its SERIES: an aggregate per period bucket, or
 * every value the metric has. Those are mutually exclusive, and the card type
 * says so by having an aggregation or not — so the aggregation picker exists
 * only on the aggregated branch rather than sitting there inert on the other.
 * The DISPLAY picker is on both: how a series is drawn is a question either
 * variant can answer, and only the answers differ (a raw chart's axis cannot
 * size bars).
 *
 * Built on foundation primitives throughout. The two-option choices are
 * toggle rows rather than dropdowns: both options fit on screen, so hiding
 * one behind a click would cost a gesture and tell the user less.
 */
export function WidgetFields({
  card,
  allNames,
  effectiveMetric,
  groupableFields = [],
  onChange,
}: WidgetFieldsProps) {
  // What to restore if the user switches a chart back to Aggregated. This is
  // transient UI memory, not card data: a raw card genuinely has no
  // aggregation, so storing one on it to make the toggle feel nice would put
  // a value in the persisted definition that nothing reads.
  const [rememberedAggregation, setRememberedAggregation] =
    React.useState<NumericAggregation>(card.aggregation ?? DEFAULT_AGGREGATION);
  const [rememberedDisplay, setRememberedDisplay] =
    React.useState<ChartDisplayType>(
      (card.kind === "chart" ? card.display : undefined) ?? DEFAULT_DISPLAY,
    );

  // What the Title input shows: exactly what was typed. The card gets it
  // trimmed, so an input bound to the card would eat the space just typed
  // between two words. Seeded once, which holds because the editor remounts
  // per widget.
  const [titleText, setTitleText] = React.useState(card.title ?? "");

  const seriesHintId = React.useId();

  /** What the Group by picker offers: clearing first, then the groupable
   *  fields in the order the dashboard sorted them. Both chart variants offer
   *  the same list — each resolves the field server-side, one to split its
   *  buckets and the other to tag its data points. */
  const groupByOptions = React.useMemo(
    () => [NO_GROUPING, ...groupableFields.map((field) => field.field)],
    [groupableFields],
  );

  const isChart = card.kind === "chart";
  // Narrowed once, so the aggregation-dependent fields below are built from a
  // card the type system agrees HAS an aggregation — a trend tile or an
  // aggregated chart. `null` is exactly the raw chart card.
  const aggregatedCard = card.aggregation === undefined ? null : card;
  const isRawChart = aggregatedCard === null;
  const seriesValue: SeriesKind = isRawChart ? "raw" : "aggregated";
  /** What the Display control offers for the variant this card currently is. */
  const displayOptions = React.useMemo(
    () => displayOptionsFor(isRawChart),
    [isRawChart],
  );

  /** Swap a chart card between its two variants. Builds the target variant
   *  explicitly rather than spreading and patching, so the card handed to
   *  `onChange` can never be a half-converted shape carrying both an
   *  aggregation and the absence that means "raw". */
  const changeSeries = (next: SeriesKind, chartCard: ChartCardConfig) => {
    // The two variant-specific fields are destructured OUT rather than set to
    // undefined, so their keys are genuinely absent — that absence is what
    // distinguishes a raw chart from an aggregated one, and a key present with
    // an undefined value would be a half-converted card.
    //
    // Everything else rides along in `shared` without being named. Naming the
    // survivors by hand is exactly how `color` was silently dropped on every
    // series change: it was added to the schema later, and this list was never
    // told about it.
    const {
      aggregation: _aggregation,
      display: _display,
      ...shared
    } = chartCard;
    // The display survives the swap in both directions, but only as far as the
    // target variant can draw: `rawDisplay` drops a bar (and the defaulted
    // line) on the way to raw, and `rememberedDisplay` puts whichever it was
    // back on the way home.
    const raw = rawDisplay(rememberedDisplay);
    onChange(
      next === "raw"
        ? { ...shared, ...(raw === undefined ? {} : { display: raw }) }
        : {
            ...shared,
            aggregation: rememberedAggregation,
            display: rememberedDisplay,
          },
    );
  };

  /** Set the chart's display, remembering it for a later series swap.
   *
   *  An aggregated chart always carries one. A raw chart carries only what its
   *  axis can draw and the default cannot be inferred from — see
   *  {@link rawDisplay} — and the key is REBUILT away rather than set to
   *  `undefined`, the same way the Group by control clears its own: a key
   *  present with an undefined value persists into the JSONB and reads back
   *  as a choice nothing made. */
  const changeDisplay = (
    next: ChartDisplayType,
    chartCard: ChartCardConfig,
  ) => {
    setRememberedDisplay(next);
    if (chartCard.aggregation !== undefined) {
      onChange({ ...chartCard, display: next });
      return;
    }
    const { display: _dropped, ...rest } = chartCard;
    const raw = rawDisplay(next);
    onChange(raw === undefined ? rest : { ...rest, display: raw });
  };

  /** Set the card's own title, or clear it back to the generated one. A blank
   *  title is REBUILT away rather than kept as an empty string, the same way
   *  the Group by control clears its own key: absent is what means
   *  "generated". */
  const changeTitle = (next: string) => {
    setTitleText(next);
    const trimmed = next.trim();
    const { title: _title, ...untitled } = card;
    onChange(trimmed === "" ? untitled : { ...card, title: trimmed });
  };

  return (
    <div className={styles.fields}>
      {/* First, where the title sits on the card itself. The placeholder is
          the title the card gets when this is left empty. */}
      <Field name="title" label="Title">
        <TextInput
          aria-label="Title"
          value={titleText}
          placeholder={getCardTitle(effectiveMetric, card.aggregation)}
          onChange={(event) => changeTitle(event.target.value)}
        />
      </Field>

      {/* The colour belongs beside the signal it colours, not in a row of
          its own — see SignalColorDot. Charts only: colour is a plotted-series
          concern, and a trend tile has no line to colour. */}
      <div className={styles.signal} data-testid="widget-field-metric">
        <Combobox
          className={styles.signalMetric}
          label={<span className={styles.label}>Signal</span>}
          items={allNames}
          value={effectiveMetric}
          // A null clear passes straight through: a card's metric is nullable,
          // and absent means "fall back to a resolved definition".
          onValueChange={(metric) => onChange({ ...card, metric })}
        >
          <Combobox.TriggerInput placeholder="Search metrics" />
          <Combobox.Content>
            <Combobox.Empty />
            <Combobox.List>
              {(name: string) => <Combobox.Item key={name} value={name} />}
            </Combobox.List>
            <Combobox.MatchCount noun="metrics" />
          </Combobox.Content>
        </Combobox>
        {isChart && (
          <div className={styles.signalColor}>
            <SignalColorDot
              value={card.color}
              onChange={(color) => onChange({ ...card, color })}
            />
          </div>
        )}
      </div>

      {isChart && (
        <Field name="series" label="One point per">
          <ToggleGroup
            size="md"
            value={[seriesValue]}
            onValueChange={([next]) => {
              // Base UI reports an empty array when the active toggle is
              // pressed again; the series is not an optional choice, so treat
              // that as "no change" rather than as a third state.
              if (next !== undefined) {
                changeSeries(next, card);
              }
            }}
            aria-label="One point per"
            aria-describedby={seriesHintId}
          >
            {SERIES_OPTIONS.map((option) => (
              <Toggle key={option.value} value={option.value}>
                {option.label}
              </Toggle>
            ))}
          </ToggleGroup>
          <p id={seriesHintId} className={styles.hint}>
            {SERIES_HINTS[seriesValue]}
          </p>
        </Field>
      )}

      {aggregatedCard !== null && (
        <div data-testid="widget-field-aggregation">
          <Combobox
            label={<span className={styles.label}>Aggregation</span>}
            items={NUMERIC_AGGREGATIONS}
            value={aggregatedCard.aggregation}
            size="md"
            onValueChange={(aggregation) => {
              // Unlike the metric, an aggregated card has no "no aggregation"
              // state — that IS the raw variant, reached by the series toggle.
              // So a clear is nothing to apply.
              if (aggregation === null) {
                return;
              }
              setRememberedAggregation(aggregation);
              onChange({ ...aggregatedCard, aggregation });
            }}
          >
            <Combobox.TriggerInput />
            <Combobox.Content>
              <Combobox.Empty />
              <Combobox.List>
                {(name: NumericAggregation) => (
                  <Combobox.Item key={name} value={name} />
                )}
              </Combobox.List>
            </Combobox.Content>
          </Combobox>
        </div>
      )}

      {/* Both chart variants: how a series is DRAWN is a question either can
          answer. They differ only in the answers on offer — a raw chart's
          time-proportional axis cannot size bars, so that one is left out
          there (see {@link displayOptionsFor}). */}
      {card.kind === "chart" && (
        <Field name="display" label="Display">
          <ToggleGroup
            size="md"
            // The card's own display, or the default line — both when it has
            // none (every raw chart until the user picks one) and when it
            // names one this variant does not offer, which no toggle here
            // could show as pressed.
            value={[
              card.display !== undefined &&
              displayOptions.some((option) => option.value === card.display)
                ? card.display
                : DEFAULT_DISPLAY,
            ]}
            onValueChange={([next]) => {
              if (next === undefined) {
                return;
              }
              changeDisplay(next, card);
            }}
            aria-label="Display"
          >
            {displayOptions.map((option) => (
              <Toggle key={option.value} value={option.value}>
                {option.label}
              </Toggle>
            ))}
          </ToggleGroup>
        </Field>
      )}

      {/* Both chart variants: grouping is a property of the SERIES, which both
          draw, not of the aggregation. The list going empty is what hides the
          control, rather than offering a dead end. */}
      {isChart && groupByOptions.length > 1 && (
        <div data-testid="widget-field-group-by">
          <Combobox
            label={<span className={styles.label}>Group by</span>}
            items={groupByOptions}
            value={card.group_by ?? NO_GROUPING}
            size="md"
            onValueChange={(next) => {
              // A cleared input and the explicit "no grouping" row mean the
              // same thing, so both land on a card with no `group_by` key.
              if (next === null || next === NO_GROUPING) {
                const { group_by: _groupBy, ...ungrouped } = card;
                onChange(ungrouped);
                return;
              }
              onChange({ ...card, group_by: next });
            }}
          >
            <Combobox.TriggerInput />
            <Combobox.Content>
              <Combobox.Empty />
              <Combobox.List>
                {(name: string) => <Combobox.Item key={name} value={name} />}
              </Combobox.List>
            </Combobox.Content>
          </Combobox>
        </div>
      )}
    </div>
  );
}

/** A heading above a control that names itself with `aria-label`. Shares
 *  `styles.label` with the labels handed to the Comboboxes above, so every
 *  field in the column is introduced the same way. */
function Field({
  name,
  label,
  children,
}: {
  /** Stable handle for tests, independent of the visible label. Labels are
   *  product copy and get reworded; a test that locates a control by its
   *  wording breaks on the rewording rather than on a real regression. */
  name: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.field} data-testid={`widget-field-${name}`}>
      <span className={styles.label}>{label}</span>
      {children}
    </div>
  );
}
